import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, MapPin, Phone, Mail, Clock, MessageCircle, Send, CheckCircle } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully! We will get back to you soon.');
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      }, 3000);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again or contact us via WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#FAFFEE] to-[#EDEDFE] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#D3FF62]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 -left-40 w-96 h-96 bg-[#CACAFC]/30 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-[#0C6951] font-semibold uppercase tracking-wider">
              <Link to="/" className="hover:text-[#004534]">Home</Link>
              <ChevronRight className="w-4 h-4" />
              Contact
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#004534] mt-4 mb-6 leading-tight">
              Get In Touch
            </h1>
            <p className="text-xl text-[#0C6951] leading-relaxed">
              We value our customers and love hearing from them. Feel free to reach out anytime!
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-[#FAFAFF] rounded-3xl p-8 md:p-10">
              <h2 className="text-2xl font-bold text-[#004534] mb-6">Send Us a Message</h2>
              
              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-20 h-20 bg-[#D3FF62] rounded-full flex items-center justify-center mb-6">
                    <CheckCircle className="w-10 h-10 text-[#004534]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#004534] mb-2">Message Sent!</h3>
                  <p className="text-[#807979]">We'll get back to you as soon as possible.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#004534] mb-2">Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-[#DDDDDD] focus:outline-none focus:border-[#004534] transition-colors"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#004534] mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-[#DDDDDD] focus:outline-none focus:border-[#004534] transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#004534] mb-2">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-[#DDDDDD] focus:outline-none focus:border-[#004534] transition-colors"
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#004534] mb-2">Subject *</label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-[#DDDDDD] focus:outline-none focus:border-[#004534] transition-colors"
                        placeholder="How can we help?"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#004534] mb-2">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-xl border border-[#DDDDDD] focus:outline-none focus:border-[#004534] transition-colors resize-none"
                      placeholder="Tell us more about your requirements..."
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#004534] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#0C6951] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#004534] mb-6">Contact Information</h2>
                <p className="text-[#807979] leading-relaxed">
                  MLDC Agro values its customers and loves hearing from them. We welcome your questions, feedback, and ideas — feel free to reach out anytime!
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D3FF62] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#004534]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#004534] mb-1">Address</h3>
                    <p className="text-[#807979]">
                      {companyInfo.address.line1}<br />
                      {companyInfo.address.line2}<br />
                      {companyInfo.address.line3}<br />
                      {companyInfo.address.line4}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D3FF62] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#004534]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#004534] mb-1">Phone</h3>
                    <a href={`tel:${companyInfo.phone}`} className="text-[#807979] hover:text-[#004534] transition-colors">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D3FF62] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#004534]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#004534] mb-1">Email</h3>
                    <a href={`mailto:${companyInfo.email}`} className="text-[#807979] hover:text-[#004534] transition-colors">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D3FF62] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#004534]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#004534] mb-1">Business Hours</h3>
                    <p className="text-[#807979]">
                      {companyInfo.hours.days}<br />
                      {companyInfo.hours.open} - {companyInfo.hours.close}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* WhatsApp CTA */}
              <a
                href={`https://wa.me/${companyInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-[#25D366] text-white p-6 rounded-2xl hover:bg-[#20bd5a] transition-all duration-200 group"
              >
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-semibold">Message us on WhatsApp</div>
                  <div className="text-white/80 text-sm">Quick response guaranteed</div>
                </div>
              </a>
              
              {/* Map Embed with Link */}
              <a
                href={companyInfo.mapLink}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-2xl overflow-hidden h-64 relative group"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3507.0434638716354!2d77.50361827549786!3d28.47313257575082!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cea64ffffffff%3A0x91ede80f8a8eb4f7!2sIthum%20Galleria!5e0!3m2!1sen!2sin!4v1706086800000!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="MLDC Agro Office Location"
                />
                <div className="absolute inset-0 bg-[#004534]/0 group-hover:bg-[#004534]/10 transition-all duration-300 flex items-center justify-center">
                  <span className="bg-white px-4 py-2 rounded-full font-medium text-[#004534] opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                    Open in Google Maps
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
